import { useRef } from 'react';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

export type CertificateTemplate = {
    imageUrl: string;
    nameTop: number;
    nameSize: number;
    nameColor: string;
};

const s3BaseUrl = `${import.meta.env.VITE_AWS_S3 || ''}`.replace(/\/$/, '');
const mediaUrl = (path?: string | null) => {
    const normalized = String(path || '').trim();
    if (!normalized) return '';
    if (/^(https?:|blob:|data:)/.test(normalized)) return normalized;
    return `${s3BaseUrl}/${normalized.replace(/^\//, '')}`;
};

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    template: CertificateTemplate;
    name: string;
};

const CertificateView = ({ open, onOpenChange, template, name }: Props) => {
    const downloading = useRef(false);
    const imageSrc = mediaUrl(template.imageUrl);

    const download = async () => {
        if (downloading.current) return;
        downloading.current = true;
        try {
            const image = new window.Image();
            image.crossOrigin = 'anonymous';
            await new Promise<void>((resolve, reject) => {
                image.onload = () => resolve();
                image.onerror = () => reject(new Error('Unable to load the certificate template.'));
                image.src = imageSrc;
            });
            const canvas = document.createElement('canvas');
            canvas.width = image.naturalWidth || 1414;
            canvas.height = image.naturalHeight || 1000;
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('Canvas is not supported in this browser.');
            ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
            // The overlay uses nameSize px against an on-screen preview ~PREVIEW_WIDTH wide;
            // scale it to the full-resolution canvas so the download matches the preview.
            const PREVIEW_WIDTH = 700;
            const fontSize = template.nameSize * (canvas.width / PREVIEW_WIDTH);
            ctx.font = `600 ${fontSize}px Georgia, "Times New Roman", serif`;
            ctx.fillStyle = template.nameColor || '#111111';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(name, canvas.width / 2, (template.nameTop / 100) * canvas.height);
            const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
            if (!blob) throw new Error('Could not generate the certificate image.');
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `certificate-${name.replace(/\s+/g, '-').toLowerCase() || 'agent'}.png`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (err: any) {
            toast.error(err?.message || 'Could not download the certificate.');
        } finally {
            downloading.current = false;
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Certificate of Completion</DialogTitle>
                    <DialogDescription>Congratulations on completing all training stages.</DialogDescription>
                </DialogHeader>
                <div className="relative overflow-hidden rounded-md border bg-white">
                    <div className="relative w-full">
                        <img src={imageSrc} alt="Certificate" className="w-full" />
                        <div
                            className="absolute left-1/2 w-[80%] -translate-x-1/2 -translate-y-1/2 text-center font-serif font-semibold"
                            style={{ top: `${template.nameTop}%`, fontSize: `${template.nameSize}px`, color: template.nameColor }}
                        >
                            {name}
                        </div>
                    </div>
                </div>
                <div className="flex justify-end">
                    <Button onClick={download}>
                        <Download className="mr-2 h-4 w-4" />
                        Download
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CertificateView;
